import {
  apiClient,
  ApiResponse,
  mapClient,
  pcMilerClient,
  v3Client,
} from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { ToastType } from '@components/Toast/Toast';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { updateLoadPropertyAtIndex } from '@store/orders/actions/orderFormActions';
import { LastUsedTopStopModel } from '@typeDefinitions/stopTypes';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';
import { pcMilerZipCountry } from '@utils/pcMilerLocation';
import { handleToast } from '@utils/utils';
import { LocationSelect } from '@views/carrierMatching/types';
import {
  ICityState,
  IZip,
} from '@views/order/components/carrier/tracking/Tracking';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { orderStore, setOrderStore } from '@store/orders/store';
import {
  AppointmentReminderModel,
  GeocodeModel,
  ILatLon,
  ILoadTender,
  loadStatus,
  MileageResult,
  NearbyCarrierRequestViewModel,
  NearbyCarriersReturn,
  PcMilerResponse,
  ShareCapacityResult,
  ShareUnShareCapacityPayload,
} from '@store/orders/types';

import { buildSourceTrackingConfig } from './serviceHelpers';

export async function setRateConRecieved(loadId: number, recieved: boolean) {
  try {
    await apiClient.post(
      `/load/RateConRecieved/${loadId}?recieved=${recieved}`,
      undefined,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsRateCon,
      ]),
    );
    handleToast(ToastType.Success, 'Rate Confirmation Status Changed');
    return true;
  } catch (error: unknown) {
    logError('setRateConReceived', error as Error);
    handleToast(ToastType.Error, 'Failed to Change Rate Confirmation Status');
  }
}

export async function postLoadStatus(
  loadStatus: loadStatus,
  changeTrackingSource?: ChangeTrackingSource,
): Promise<boolean | undefined> {
  try {
    const response: ApiResponse<boolean> = await apiClient.post<
      loadStatus,
      ApiResponse<boolean>
    >(
      '/load/changestatus',
      loadStatus,
      buildSourceTrackingConfig(changeTrackingSource),
    );
    if (response.isSuccess) {
      handleToast(ToastType.Success, 'Load status updated!');
    } else {
      handleToast(ToastType.Error, 'Failed to update status');
    }
    return response.isSuccess;
  } catch (error: unknown) {
    logError('postLoadStatus', error as Error);

    throw createError(error, 'Failed to update status');
  }
}

export const getBookedStatus = async (loadId: number) => {
  try {
    const response: { value: number[] } = await apiClient.get(
      `/capacity/booked/bid/load/${loadId}`,
    );
    if ('isSuccess' in response && response.isSuccess === false) {
      throw 'errors' in response ? response.errors : [];
    }
    if (Boolean(response.value[0])) {
      setOrderStore(
        'order',
        'loads',
        (load) => load.id === loadId,
        'carrierMatchId',
        response.value[0],
      );
      handleToast(ToastType.Success, 'This load is Carrier Matched');
    }
  } catch (error) {
    logError('getBookedStatus', error as Error);
    throw createError(error, 'Failed to get booked status');
  }
};

export async function fetchTopStops(
  customerId: number,
): Promise<LastUsedTopStopModel[]> {
  try {
    const response = await apiClient.get<ApiResponse<LastUsedTopStopModel[]>>(
      `/stop/GetTop/${customerId}`,
    );
    if ('value' in response) {
      return response.value as LastUsedTopStopModel[];
    }
    throw new Error('Failed to fetch top stops');
  } catch (error: unknown) {
    logError('fetchTopStops', error as Error);
    throw createError(error, 'Failed to fetch top stops');
  }
}

export async function fetchPastStops(
  customerId: number,
): Promise<LastUsedTopStopModel[]> {
  try {
    const response = await apiClient.get<ApiResponse<LastUsedTopStopModel[]>>(
      `/stop/GetPast/${customerId}`,
    );

    if ('value' in response) {
      return response.value as LastUsedTopStopModel[];
    }

    throw new Error('Failed to fetch past stops');
  } catch (error: unknown) {
    logError('fetchPastStops', error as Error);
    throw createError(error, 'Failed to fetch past stops');
  }
}

export async function fetchCoordinatesFromZip(
  addressArray: IZip[],
  type?: string,
): Promise<ILatLon[]> {
  try {
    const response = await mapClient.post('/locations/address/batch', {
      Locations: addressArray,
    });
    if (type !== 'EDILoadTender') {
      const someErrorExists = (response.data as ILatLon[]).some(
        (data: ILatLon) => data.Errors.length > 0,
      );
      if (someErrorExists) {
        const hasPoBoxError = (response.data as ILatLon[]).some(
          (data: ILatLon) =>
            data.Errors.length > 0 && data.Errors.some((e) => e.Code == 88),
        );
        if (hasPoBoxError)
          throw Error(
            'PO Box Zip are not supported. Warning: Map pins and mileage are unavailable. Load save is still possible',
          );
        throw Error(
          'Failed to fetch coordinates. Warning: Map pins and mileage are unavailable. Load save is still possible',
        );
      }
    }
    return response.data as ILatLon[];
  } catch (error: unknown) {
    logError('fetchCoordinatesFromZip', error as Error);
    throw createError(error, 'Failed to fetch coordinates');
  }
}

export async function fetchCoordinatesFromAddress(
  addressArray: ICityState[],
): Promise<ILatLon[]> {
  try {
    const response = await mapClient.post('/locations/address/batch', {
      Locations: addressArray,
    });
    return response.data as ILatLon[];
  } catch (error: unknown) {
    logError('fetchCoordinatesFromAddress', error as Error);
    throw createError(error, 'Failed to fetch coordinates');
  }
}

export async function fetchLoadTender(
  orderId: number | string,
): Promise<ILoadTender> {
  try {
    const response = await v3Client.get(
      `EDI/GetTenderByOrder/${orderId}?id=${orderId}`,
    );
    return response as unknown as ILoadTender;
  } catch (error: unknown) {
    logError('fetchLoadTender', error as Error);
    throw createError(error, 'Failed to fetch offices');
  }
}

export async function voidLoad(loadId: number) {
  try {
    const response: V4ApiResponse<unknown> = await apiClient.post(
      `Order/DeleteLoad/${loadId}`,
      undefined,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsVoidLoad,
      ]),
    );
    if (response.isSuccess) {
      const loadIndex = orderStore.order.loads.findIndex(
        (l) => l.id === loadId,
      );
      if (loadIndex >= 0) {
        updateLoadPropertyAtIndex({ status: 'Void' }, loadIndex);
      }
      handleToast(ToastType.Success, 'Load Voided');
    } else {
      throw response.errors;
    }
  } catch (error: unknown) {
    logError('voidLoad', error as Error);
    throw createError(error, 'Failed to void load');
  }
}

export async function unlinkEDITender(
  shipmentId: string | undefined,
  orderId: string,
) {
  try {
    await apiClient.post(
      'order/UnlinkLoadTender',
      {
        shipmentId: shipmentId,
        orderId: orderId,
      },
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.orderDetails.ediUpdatesUnlinkTender,
      ]),
    );
  } catch (error: unknown) {
    logError('unlinkEDITender', error as Error);
    throw createError(error, 'Failed to unlink the EDI');
  }
}

export async function shareAndUnShareLoadCapacity(
  payload: ShareUnShareCapacityPayload,
) {
  try {
    const isPolicy = isFeatureFlagEnabled(FeatureFlags.PolicySharedCapacity)();
    const response = await apiClient.post(
      isPolicy ? 'load/ShareCapacity/policy' : 'load/ShareCapacity/v2',
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsShareCapacity,
      ]),
    );
    return response as unknown as {
      error: string[];
      isSuccess: boolean;
      value: ShareCapacityResult;
      isFailure: boolean;
    };
  } catch (error: unknown) {
    logError('shareAndUnshareLoadCapacity', error as Error);
    throw createError(error, 'Failed to share load capacity');
  }
}

export async function getNearbyCarriers(
  payload: NearbyCarrierRequestViewModel,
) {
  try {
    if (payload.radius === undefined) {
      payload.radius = 60;
    }
    const response = await apiClient.post<NearbyCarriersReturn[]>(
      'order/load/nearbycarriers',
      payload,
    );

    if ('value' in response) {
      return response.value;
    }
  } catch (error: unknown) {
    logError('getNearbyCarriers', error as Error);
    throw createError(error, 'Failed to fetch nearby carriers');
  }
}

export async function calculateMileage(
  stops: LastUsedTopStopModel[] | Record<string, string>[] | undefined,
  params: string | number | null,
) {
  try {
    const geoCoordinates = await getGeoCoordinates(stops);
    const formatted = Array.isArray(geoCoordinates)
      ? geoCoordinates.map(
          (c: {
            coords: {
              lat: string;
              lon: string;
            };
          }) => {
            return `${c.coords.lon},${c.coords.lat}`;
          },
        )
      : [];
    const shortMiles: Record<string, string | number>[] =
      await pcMilerClient.get(
        `route/routeReports?reports=CalcMiles&stops=${formatted.join(
          ';',
        )}&dataVersion=${params !== null ? params : ''}&routeType=Shortest`,
      );

    const practicalMiles: Record<string, string | number>[] =
      await pcMilerClient.get(
        `route/routeReports?reports=CalcMiles&stops=${formatted.join(
          ';',
        )}&dataVersion=${params !== null ? params : ''}&routeType=Practical`,
      );

    if (Array.isArray(shortMiles) && Array.isArray(practicalMiles)) {
      return {
        shortMiles: shortMiles[0].tMiles,
        practicalMiles: practicalMiles[0].tMiles,
      };
    }
  } catch (error: unknown) {
    logError('calculateMilage', error as Error);
    throw createError(error, 'Failed to get mileage');
  }
}

export async function calculateMileageWithSegments(
  stops: LastUsedTopStopModel[],
  pcMilerVersion: string | number | null,
  observabilityTag: string,
): Promise<MileageResult | undefined> {
  if (stops.length < 2) return undefined;

  const stopSegments: MileageResult['stopSegments'] = [];
  let totalPracticalMiles = 0;
  let totalShortMiles = 0;

  if (stops.length > 2) {
    const totalResponse = await calculateMileage(stops, pcMilerVersion);
    totalPracticalMiles = Number(totalResponse?.practicalMiles) || 0;
    totalShortMiles = Number(totalResponse?.shortMiles) || 0;
  }

  for (let i = 0; i < stops.length; i++) {
    if (i === stops.length - 1) {
      stopSegments.push({
        practicalMileageToNextStop: null,
        shortestMileageToNextStop: null,
      });
      continue;
    }

    const segmentResponse = await calculateMileage(
      [stops[i], stops[i + 1]],
      pcMilerVersion,
    );

    logError(
      `MileageObservability(${observabilityTag})`,
      new Error('Observability for mileage'),
      {
        shortMiles: segmentResponse?.shortMiles ?? null,
        practicalMiles: segmentResponse?.practicalMiles ?? null,
        coords: stops,
      },
    );

    stopSegments.push({
      practicalMileageToNextStop: Number(segmentResponse?.practicalMiles) || 0,
      shortestMileageToNextStop: Number(segmentResponse?.shortMiles) || 0,
    });
  }

  if (stops.length === 2) {
    totalPracticalMiles = stopSegments[0].practicalMileageToNextStop ?? 0;
    totalShortMiles = stopSegments[0].shortestMileageToNextStop ?? 0;
  }

  return {
    totalPracticalMiles,
    totalShortMiles,
    stopSegments,
  };
}

export async function getGeoCoordinates(
  stops: LastUsedTopStopModel[] | Record<string, string>[] | undefined,
) {
  try {
    const params: PcMilerResponse[] | undefined = stops?.map((stop) => {
      const country = pcMilerZipCountry(stop.countryCode);
      const countryAddress: PcMilerResponse['address'] = {};
      if (country.countryAbbreviation) {
        countryAddress.countryAbbreviation = country.countryAbbreviation;
      }
      if (country.countryPostalFilter !== undefined) {
        countryAddress.countryPostalFilter = country.countryPostalFilter;
      }
      return stop.zip
        ? { address: { zip: stop.zip, ...countryAddress } }
        : {
            address: {
              city: stop.city,
              state: stop.state,
              ...countryAddress,
            },
          };
    });

    const response: PcMilerResponse[] = await pcMilerClient.post(
      'locations/address/batch',
      {
        Locations: params,
      },
    );

    //For error handling start
    response.forEach((item, index) => {
      const filteredErrors = (item.errors || []).filter(
        (error) => error.type !== 0,
      );

      if (params) {
        params[index].errors = filteredErrors;
      }
    });

    (params || []).forEach((error) => {
      if (error.address.zip != null) {
        if (error.errors && error.errors.length > 0) {
          error.errors[0].description +=
            ' for ' +
            (Boolean(error.address.zip)
              ? error.address.zip
              : `${error.address.state}`);
        }
      }
    });
    const errors = params?.flatMap((param) => param.errors);
    //error handling end

    if (errors && errors.length > 0) {
      throw errors.map((error) => ({
        errorMessage: error?.description ?? 'Failed to get coordinates',
      }));
    }
    if (errors && errors.length < 1) {
      return response as unknown as Record<string, string | number>;
    }
  } catch (error: unknown) {
    logError('getGeoCoordinates', error as Error);
    throw createError(error, 'Failed to get mileage');
  }
}

export async function setAppointmentReminder(
  payload: AppointmentReminderModel,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .orderDetails.stopsSetAppointmentReminder,
) {
  try {
    const response = await apiClient.post(
      'Reminder',
      payload,
      buildChangeTrackingRequestConfig([changeTrackingSource]),
    );
    return response as unknown as Record<string, string | number>;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to set appointment reminder');
    handleToast(ToastType.Error, e.message);
  }
}

export async function getGoogleGeocode(payload: GeocodeModel) {
  try {
    const response: V4ApiResponse<GeocodeModel> = await apiClient.post(
      'Location/Geocode',
      payload,
    );
    return response.value as unknown as GeocodeModel;
  } catch (error: unknown) {
    logError('getGoogleGeocode', error as Error);
    throw createError(error, 'Failed to get geocode');
  }
}

export const countryToCountryCode = {
  usa: 'US',
  'united states of america': 'US',
  'united states': 'US',
  mex: 'MX',
  mexico: 'MX',
  can: 'CA',
  canada: 'CA',
};

export const getLocationTimezone = async (country: string, q: string) => {
  try {
    if (q.includes('-')) {
      q = q.replace(/-[^\b]*$/, '');
    }

    country =
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      countryToCountryCode[
        country.toLowerCase() as keyof typeof countryToCountryCode
      ] ?? country;

    const url = `capacity/autocomplete?country=${country}&term=${q}`;
    const resp: { value: LocationSelect[] } = await apiClient.get(url);

    if (resp.value.length === 0) {
      return;
    }

    return resp.value[0].timezone;
  } catch (error) {
    logError('getLocationTimezone', error as Error);
    throw createError(
      error,
      `Failed to fetch timezone for location ${country} "${q}"`,
    );
  }
};
